import React, { useEffect, useRef, useState } from 'react';
import {
  useLibkeyNomadMigrationEligible,
  useLibkeyVisibleReferenceCount,
} from '../LibkeyProvider';
import styles from './LibkeyNomadMigration.module.scss';
import { harvestNomadLibraryId } from './harvest';
import { useNomadMigrationDismissal } from './dismissal';
import {
  showMigrationErrorToast,
  showMigrationHelpToast,
  dismissMigrationErrorToast,
  showMigrationSuccessToast,
  markMigrationSucceeded,
  consumeMigrationSucceeded,
} from './migrationToasts';
import {
  fetchLibkeyLibraries,
  saveLibkeySelection,
} from 'query/study/LibKey/Libraries';
import {
  Button,
  Link,
  Message,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Strong,
  Text,
} from 'components/core';
import { Inline, Stack } from 'components/layout';

type HarvestedLibrary = { id: number; name: string | null };

// Which surface to show once a Nomad library has been harvested:
// - 'popup'  the full migration dialog (first visit, or re-opened from banner)
// - 'banner' a compact nudge at the top of the list (after "Maybe later")
// - 'hidden' nothing at all, because the reviewer closed the banner — a
//            persistent per-device dismissal of the whole nudge
type View = 'popup' | 'banner' | 'hidden';

export type LibkeyNomadMigrationProps = {
  harvest?: typeof harvestNomadLibraryId;
  fetchLibraries?: typeof fetchLibkeyLibraries;
  saveSelection?: typeof saveLibkeySelection;
  reloadPage?: () => void;
};

/**
 * Nudge for reviewers still using the LibKey Nomad browser extension: silently
 * read their library id out of the extension's injected buttons, then offer to
 * carry it over to Covidence's built-in LibKey integration.
 *
 * First visit shows a modal. "Switch now" persists the library and reloads so
 * the native buttons render (and the `ref-ids` hook Nomad keys off stops being
 * emitted). "Maybe later" (and the modal's close X / ESC) records a per-device
 * dismissal and steps down to a quiet banner at the top of the list, whose
 * "Switch now" link re-opens the modal — so a dismissed-but-still-eligible
 * reviewer can pick the migration back up. Closing the banner itself records a
 * stronger per-device dismissal that suppresses the nudge entirely. Renders
 * nothing until a Nomad button is actually harvested.
 */
export const LibkeyNomadMigration = ({
  harvest = harvestNomadLibraryId,
  fetchLibraries = fetchLibkeyLibraries,
  saveSelection = saveLibkeySelection,
  reloadPage = () => window.location.reload(),
}: LibkeyNomadMigrationProps) => {
  // From LibkeyProvider: the LibKey feature flags are on AND the reviewer has
  // not selected their own library yet (the Open Alex default may still be
  // rendering native links). The localStorage dismissal only decides
  // popup-vs-banner on top of this, never whether we engage at all.
  const eligible = useLibkeyNomadMigrationEligible();
  // The extension injects its buttons onto reference rows, so there is nothing
  // to harvest until rows have rendered. Gating on this (rather than harvesting
  // straight away on mount) stops the harvest timeout being burned while an
  // extraction list's studies are still loading.
  const visibleReferenceCount = useLibkeyVisibleReferenceCount();
  // Per-device dismissal flags, already scoped to the current reviewer so two
  // reviewers sharing a browser don't inherit each other's state.
  const {
    isDismissed,
    markDismissed,
    isBannerDismissed,
    markBannerDismissed,
  } = useNomadMigrationDismissal();
  const [library, setLibrary] = useState<HarvestedLibrary | null>(null);
  const [view, setView] = useState<View>('popup');
  const [isSaving, setIsSaving] = useState(false);

  // A successful switch reloads the page; after that reload this component
  // re-mounts but is no longer eligible (the reviewer now has a library), so
  // it would otherwise render nothing. Surface the confirmation toast here on
  // mount, reading the one-shot signal the pre-reload save left behind.
  useEffect(() => {
    if (consumeMigrationSucceeded()) {
      showMigrationSuccessToast();
    }
  }, []);

  // Harvest guards, both refs so they survive re-renders and StrictMode's
  // double-mount. `harvested` latches once a library id is found, ending all
  // further attempts; `harvesting` blocks a second concurrent attempt while one
  // is in flight (its DOM observer already covers any rows that render during
  // its window).
  const harvested = useRef(false);
  const harvesting = useRef(false);

  // Counts failed saves so the second failure escalates from "Try again" to a
  // pointer at the LibKey settings page: some failures (a harvested library
  // that isn't in Covidence's list) can never succeed on retry.
  const saveFailures = useRef(0);

  // Mirrors LibkeyProvider's mounted ref: re-asserted on setup so a harvest
  // started by StrictMode's first mount can still resolve into the remount.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    // Harvest even when previously dismissed: the banner should only appear
    // when Nomad is still in use (a button is present to read), and re-opening
    // the popup from it needs the harvested library. So presence detection
    // runs regardless; only the resulting surface depends on the dismissal.
    //
    // Wait for reference rows before harvesting, and re-run when their count
    // grows: an extraction list mounts this component while its studies are
    // still loading, so a single harvest fired on mount can time out before any
    // row (and therefore any Nomad button) exists. Re-attempting as rows appear
    // — first render, then pagination — means late-arriving buttons are still
    // caught.
    if (
      !eligible ||
      harvested.current ||
      harvesting.current ||
      visibleReferenceCount === 0
    ) {
      return;
    }
    harvesting.current = true;

    const run = async () => {
      const libraryId = await harvest();
      harvesting.current = false;
      if (libraryId === null || !mounted.current) {
        // Timed out before a button appeared (or unmounted). Leave `harvested`
        // unset so a later growth in the visible row count retries.
        return;
      }
      harvested.current = true;

      // Resolve the harvested id to a display name for the prompt copy — best
      // effort only. The libraries listing is filtered server-side, so a Nomad
      // library can legitimately be absent from it (and the request can 502 /
      // time out). We do NOT gate on resolution: show the library unnamed
      // rather than skip it.
      let libraryName: string | null = null;
      try {
        const libraries = await fetchLibraries();
        libraryName =
          libraries.find((entry) => entry.id === libraryId)?.name ?? null;
      } catch {
        // Listing unavailable — fall through and show the library unnamed.
      }
      if (!mounted.current) {
        return;
      }

      setLibrary({ id: libraryId, name: libraryName });
      // A reviewer who closed the banner sees nothing; one who said "Maybe
      // later" gets the quiet banner; everyone else gets the popup straight
      // away.
      if (isBannerDismissed()) {
        setView('hidden');
      } else {
        setView(isDismissed() ? 'banner' : 'popup');
      }
    };
    void run();
  }, [
    eligible,
    visibleReferenceCount,
    harvest,
    fetchLibraries,
    isBannerDismissed,
    isDismissed,
  ]);

  if (!library || view === 'hidden') {
    return null;
  }

  const accept = async (event: React.MouseEvent<HTMLButtonElement>) => {
    const triggerElement = event.currentTarget;

    dismissMigrationErrorToast();
    setIsSaving(true);
    try {
      await saveSelection(library.id);
    } catch {
      // First failure could be transient, so offer a "Try again". A second
      // failure won't recover on retry (e.g. the harvested library isn't in
      // Covidence's list), so escalate to a route out: a link to the LibKey
      // settings page where they can choose a library by hand. Either way the
      // prompt stays open behind the toast.
      if (mounted.current) {
        setIsSaving(false);
        saveFailures.current += 1;
        if (saveFailures.current >= 2) {
          showMigrationHelpToast(triggerElement);
        } else {
          showMigrationErrorToast(accept, triggerElement);
        }
      }
      return;
    }
    // The reviewer now has a configured library, so reload: the native buttons
    // render, the server-side gate suppresses this prompt for good, and the
    // page stops emitting the ref-ids hook Nomad injects against. Leave a
    // one-shot signal so the post-reload mount can show the success toast.
    markMigrationSucceeded();
    reloadPage();
  };

  // "Maybe later" (also the modal's close X / ESC): record the per-device
  // dismissal and step down to the banner so the reviewer can still pick the
  // migration up later without being blocked.
  const decline = () => {
    if (isSaving) {
      return;
    }
    dismissMigrationErrorToast();
    markDismissed();
    setView('banner');
  };

  // Closing the banner: a stronger dismissal that hides the nudge for good on
  // this device.
  const dismissBanner = () => {
    markBannerDismissed();
    setView('hidden');
  };

  const reopen = () => {
    setView('popup');
  };

  if (view === 'banner') {
    return (
      <Message
        type="info"
        icon={false}
        dismissible
        onClose={dismissBanner}
        className={styles.banner}
        data-testid="libkey-nomad-migration-banner"
      >
        <Strong text="LibKey is now built into Covidence" />
        <Text size="sm">
          Still using the LibKey browser extension?{' '}
          <Link
            variant="primary"
            onClick={reopen}
            data-pendo-key="libkey-nomad-migration-banner-open"
          >
            Switch now
          </Link>
        </Text>
      </Message>
    );
  }

  return (
    <Modal
      isOpen
      size="md"
      onClose={decline}
      aria-label="LibKey is now integrated in Covidence"
    >
      <ModalHeader>LibKey is now integrated in Covidence</ModalHeader>
      <ModalBody>
        <Stack spacing={5}>
          <Text variant="neutral">
            {library.name ? (
              <>
                We noticed you&apos;re using the LibKey browser extension with{' '}
                <Strong text={library.name} />.
              </>
            ) : (
              "We noticed you're using the LibKey browser extension."
            )}
          </Text>
          <Text variant="neutral">
            Switch to use Covidence&apos;s integration and full text links will
            appear directly in your review, no extension needed.
          </Text>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Inline alignX="right" spacing={3}>
          <Button
            variant="outline"
            type="neutral"
            onClick={decline}
            disabled={isSaving}
            data-pendo-key="libkey-nomad-migration-decline"
          >
            Maybe later
          </Button>
          <Button
            variant="solid"
            type="brand"
            onClick={accept}
            isLoading={isSaving}
            data-pendo-key="libkey-nomad-migration-accept"
          >
            Switch now
          </Button>
        </Inline>
      </ModalFooter>
    </Modal>
  );
};
